import { useState, useEffect } from "react";
import AdminLayout from "@/components/feature/AdminLayout";
import { useGA4 } from "@/hooks/useGA4";
import { useAdminI18n } from "@/hooks/useAdminI18n";

const STEPS = [
  {
    number: 1,
    title: "Google Analytics-Konto erstellen",
    description: "Gehen Sie zu analytics.google.com und melden Sie sich mit Ihrem Google-Konto an. Klicken Sie auf \"Konto erstellen\" und folgen Sie den Anweisungen.",
    link: "https://analytics.google.com",
    linkLabel: "analytics.google.com öffnen",
    icon: "ri-google-fill",
  },
  {
    number: 2,
    title: "GA4-Property einrichten",
    description: "Erstellen Sie eine neue Property für Ihre Website. Wählen Sie \"Google Analytics 4\" als Property-Typ. Geben Sie Ihren Website-Namen und die URL ein.",
    icon: "ri-settings-3-line",
  },
  {
    number: 3,
    title: "Measurement ID kopieren",
    description: "Nach der Einrichtung finden Sie Ihre Measurement ID unter: Verwaltung → Property → Datenstreams → Ihr Stream → Measurement ID. Sie sieht aus wie: G-XXXXXXXXXX",
    icon: "ri-key-line",
  },
  {
    number: 4,
    title: "ID hier einfügen und speichern",
    description: "Fügen Sie Ihre Measurement ID in das Feld unten ein und klicken Sie auf \"Speichern\". Das Tracking wird sofort aktiviert.",
    icon: "ri-check-double-line",
  },
];

const METRICS = [
  { labelKey: "analytics.metric.users", icon: "ri-user-line", key: "users" },
  { labelKey: "analytics.metric.pageviews", icon: "ri-eye-line", key: "pageviews" },
  { labelKey: "analytics.metric.sessions", icon: "ri-time-line", key: "sessions" },
  { labelKey: "analytics.metric.bounce", icon: "ri-arrow-go-back-line", key: "bounce" },
];

export default function AdminAnalytics() {
  const { measurementId, isActive, saveMeasurementId, removeMeasurementId } = useGA4();
  const { t } = useAdminI18n();
  const [inputId, setInputId] = useState(measurementId);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [activeTab, setActiveTab] = useState<"setup" | "embed" | "schema">("setup");

  useEffect(() => {
    setInputId(measurementId);
  }, [measurementId]);

  const validateId = (id: string) => /^G-[A-Z0-9]{6,12}$/i.test(id.trim());

  const handleSave = () => {
    const trimmed = inputId.trim().toUpperCase();
    if (!trimmed) {
      setError("Bitte geben Sie eine Measurement ID ein.");
      return;
    }
    if (!validateId(trimmed)) {
      setError("Ungültige Measurement ID. Format: G-XXXXXXXXXX");
      return;
    }
    setError("");
    saveMeasurementId(trimmed);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const handleRemove = () => {
    removeMeasurementId();
    setInputId("");
    setShowRemoveConfirm(false);
  };

  const schemaSQL = `-- Helvetik Journal: Supabase Schema
-- Run this in your Supabase SQL Editor

-- Articles table
CREATE TABLE IF NOT EXISTS articles (
  id uuid PRIMARY KEY DEFAULT gen_random_uuid(),
  title text NOT NULL,
  subtitle text,
  slug text UNIQUE NOT NULL,
  excerpt text NOT NULL DEFAULT '',
  content text NOT NULL DEFAULT '',
  category text NOT NULL DEFAULT '',
  category_slug text NOT NULL DEFAULT '',
  subcategory text,
  author text NOT NULL DEFAULT '',
  author_slug text NOT NULL DEFAULT '',
  author_role text,
  author_bio text,
  author_image text,
  image_url text NOT NULL DEFAULT '',
  image_alt text,
  gallery text[],
  published_at timestamptz NOT NULL DEFAULT now(),
  updated_at timestamptz,
  read_time text NOT NULL DEFAULT '3 Min.',
  views integer NOT NULL DEFAULT 0,
  tags text[] NOT NULL DEFAULT '{}',
  status text NOT NULL DEFAULT 'draft'
    CHECK (status IN ('draft','pending_review','published','scheduled','archived')),
  is_featured boolean NOT NULL DEFAULT false,
  is_breaking boolean NOT NULL DEFAULT false,
  is_top_news boolean NOT NULL DEFAULT false,
  is_opinion boolean NOT NULL DEFAULT false,
  is_column boolean NOT NULL DEFAULT false,
  show_on_homepage_slider boolean NOT NULL DEFAULT false,
  show_in_top_news_column boolean NOT NULL DEFAULT false,
  show_in_category_featured boolean NOT NULL DEFAULT false,
  comments_enabled boolean NOT NULL DEFAULT true,
  ads_enabled boolean NOT NULL DEFAULT true,
  sources jsonb,
  internal_links jsonb,
  external_links jsonb,
  related_article_ids text[],
  meta_title text,
  meta_description text,
  focus_keyword text,
  canonical_url text,
  og_title text,
  og_description text,
  og_image text,
  twitter_image text,
  no_index boolean NOT NULL DEFAULT false,
  created_at timestamptz NOT NULL DEFAULT now()
);

-- Authors table
CREATE TABLE IF NOT EXISTS authors (
  id uuid PRIMARY KEY DEFAULT gen_random_uuid(),
  name text NOT NULL,
  slug text UNIQUE NOT NULL,
  role text NOT NULL DEFAULT '',
  short_bio text NOT NULL DEFAULT '',
  long_bio text NOT NULL DEFAULT '',
  email text NOT NULL DEFAULT '',
  image text NOT NULL DEFAULT '',
  article_count integer NOT NULL DEFAULT 0,
  type text NOT NULL DEFAULT 'author'
    CHECK (author_type IN ('redaktion','autor','kolumnist','gastautor','editor')),
  is_active boolean NOT NULL DEFAULT true,
  social_links jsonb NOT NULL DEFAULT '[]',
  created_at timestamptz NOT NULL DEFAULT now()
);

-- Enable Row Level Security
ALTER TABLE articles ENABLE ROW LEVEL SECURITY;
ALTER TABLE authors ENABLE ROW LEVEL SECURITY;

-- Public read access for published articles
CREATE POLICY "Public can read published articles"
  ON articles FOR SELECT
  USING (status = 'published');

-- Public read access for active authors
CREATE POLICY "Public can read active authors"
  ON authors FOR SELECT
  USING (is_active = true);

-- Authenticated users (admins) can do everything
CREATE POLICY "Authenticated users can manage articles"
  ON articles FOR ALL
  TO authenticated
  USING (true)
  WITH CHECK (true);

CREATE POLICY "Authenticated users can manage authors"
  ON authors FOR ALL
  TO authenticated
  USING (true)
  WITH CHECK (true);`;

  const [schemaCopied, setSchemaCopied] = useState(false);
  const copySchema = () => {
    navigator.clipboard.writeText(schemaSQL).then(() => {
      setSchemaCopied(true);
      setTimeout(() => setSchemaCopied(false), 2500);
    });
  };

  return (
    <AdminLayout>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-hj-dark font-heading">{t("analytics.title")}</h1>
          <p className="text-sm text-hj-gray mt-0.5 font-body">
            {t("analytics.header.subtitle")}
          </p>
        </div>

        {/* Status Banner */}
        {isActive && (
          <div className="mb-5 flex items-center gap-3 px-4 py-3 bg-emerald-50 border border-emerald-200 rounded-lg">
            <div className="w-8 h-8 flex items-center justify-center rounded-full bg-emerald-100 shrink-0">
              <i className="ri-check-line text-emerald-600"></i>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-emerald-800 font-body">
                {t("analytics.status.active")}
              </p>
              <p className="text-xs text-emerald-600 font-body mt-0.5">
                {t("analytics.status.trackingId")}: <strong>{measurementId}</strong>
              </p>
            </div>
            <button
              onClick={() => setShowRemoveConfirm(true)}
              className="text-xs text-emerald-600 hover:text-red-500 transition-colors font-body whitespace-nowrap"
            >
              Entfernen
            </button>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 mb-6 bg-hj-gray-light p-1 rounded-lg w-fit">
          {[
            { key: "setup", label: t("analytics.tab.setup"), icon: "ri-bar-chart-grouped-line" },
            { key: "embed", label: t("analytics.tab.embed"), icon: "ri-code-line" },
            { key: "schema", label: t("analytics.tab.schema"), icon: "ri-database-2-line" },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key as typeof activeTab)}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-colors whitespace-nowrap ${
                activeTab === tab.key
                  ? "bg-white text-hj-dark shadow-sm"
                  : "text-hj-gray hover:text-hj-dark"
              }`}
            >
              <i className={tab.icon}></i>
              {tab.label}
            </button>
          ))}
        </div>

        {/* ── GA4 Setup Tab ── */}
        {activeTab === "setup" && (
          <div className="space-y-5">
            {/* Steps */}
            <div className="bg-white rounded-lg border border-hj-gray-border p-5 md:p-6">
              <h2 className="text-base font-bold text-hj-dark mb-5 font-heading">
                {t("analytics.setup.title")}
              </h2>
              <div className="space-y-5">
                {STEPS.map((step) => (
                  <div key={step.number} className="flex gap-4">
                    <div className="shrink-0 w-8 h-8 rounded-full bg-hj-red/10 flex items-center justify-center">
                      <span className="text-sm font-bold text-hj-red">{step.number}</span>
                    </div>
                    <div className="flex-1 min-w-0 pt-0.5">
                      <h3 className="text-sm font-semibold text-hj-dark font-body">{step.title}</h3>
                      <p className="text-sm text-hj-gray mt-1 leading-relaxed font-body">
                        {step.description}
                      </p>
                      {step.link && (
                        <a
                          href={step.link}
                          target="_blank"
                          rel="noopener noreferrer nofollow"
                          className="inline-flex items-center gap-1.5 mt-2 text-xs text-hj-red hover:underline font-body"
                        >
                          <i className="ri-external-link-line"></i>
                          {step.linkLabel}
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Input */}
            <div className="bg-white rounded-lg border border-hj-gray-border p-5 md:p-6">
              <h2 className="text-base font-bold text-hj-dark mb-1 font-heading">
                {t("analytics.input.title")}
              </h2>
              <p className="text-sm text-hj-gray mb-4 font-body">
                {t("analytics.input.subtitle")} <code className="bg-hj-gray-light px-1.5 py-0.5 rounded text-xs font-mono">G-</code>.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="flex-1">
                  <input
                    type="text"
                    value={inputId}
                    onChange={(e) => {
                      setInputId(e.target.value);
                      setError("");
                    }}
                    placeholder={t("analytics.input.placeholder")}
                    className={`w-full px-4 py-2.5 border rounded-md text-sm font-mono focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red transition-colors ${
                      error ? "border-red-400 bg-red-50" : "border-hj-gray-border"
                    }`}
                  />
                  {error && (
                    <p className="text-xs text-red-500 mt-1.5 font-body flex items-center gap-1">
                      <i className="ri-error-warning-line"></i>
                      {error}
                    </p>
                  )}
                </div>
                <button
                  onClick={handleSave}
                  className="px-5 py-2.5 bg-hj-red text-white text-sm font-medium rounded-md hover:bg-hj-red-light transition-colors whitespace-nowrap flex items-center gap-2"
                >
                  {saved ? (
                    <>
                      <i className="ri-check-line"></i>
                      {t("analytics.input.saved")}
                    </>
                  ) : (
                    <>
                      <i className="ri-save-line"></i>
                      {t("analytics.input.save")}
                    </>
                  )}
                </button>
              </div>
              <p className="text-xs text-hj-gray mt-3 font-body">
                <i className="ri-information-line mr-1"></i>
                {t("analytics.input.help")}
              </p>
            </div>

            {/* Metrics Placeholder */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {METRICS.map((m) => (
                <div key={m.key} className="bg-white rounded-lg border border-hj-gray-border p-4 text-center">
                  <div className="w-8 h-8 flex items-center justify-center rounded-full bg-hj-gray-light mx-auto mb-2">
                    <i className={`${m.icon} text-hj-gray text-sm`}></i>
                  </div>
                  <p className={`text-xl font-bold font-heading ${isActive ? "text-hj-dark" : "text-hj-gray-border"}`}>
                    {isActive ? "—" : "--"}
                  </p>
                  <p className="text-xs text-hj-gray mt-1 font-body">{t(m.labelKey)}</p>
                </div>
              ))}
            </div>

            {isActive && (
              <div className="bg-sky-50 border border-sky-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <i className="ri-information-line text-sky-500 mt-0.5"></i>
                  <div>
                    <p className="text-sm font-semibold text-sky-800 font-body">Live-Daten im GA4 Dashboard</p>
                    <p className="text-sm text-sky-700 mt-1 font-body">
                      Besuchen Sie{" "}
                      <a
                        href="https://analytics.google.com"
                        target="_blank"
                        rel="noopener noreferrer nofollow"
                        className="underline"
                      >
                        analytics.google.com
                      </a>{" "}
                      um Ihre Echtzeit-Statistiken, Besucherberichte und Conversion-Daten einzusehen.
                      Daten erscheinen in der Regel innerhalb von 24–48 Stunden.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Embed Code Tab ── */}
        {activeTab === "embed" && (
          <div className="space-y-5">
            <div className="bg-white rounded-lg border border-hj-gray-border p-5 md:p-6">
              <h2 className="text-base font-bold text-hj-dark mb-2 font-heading">
                {t("analytics.embed.title")}
              </h2>
              <p className="text-sm text-hj-gray mb-5 font-body">
                Der GA4 Tracking-Code wird automatisch in alle Seiten eingefügt, sobald Sie eine Measurement ID gespeichert haben.
                Der Code wird dynamisch über den <code className="bg-hj-gray-light px-1.5 py-0.5 rounded text-xs font-mono">useGA4</code> Hook geladen.
              </p>

              {isActive ? (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-hj-dark uppercase tracking-wider font-body">
                      Aktiver Tracking-Code
                    </span>
                    <span className="flex items-center gap-1.5 text-xs text-emerald-600 font-body">
                      <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                      Aktiv
                    </span>
                  </div>
                  <pre className="bg-hj-dark text-emerald-400 text-xs p-4 rounded-lg overflow-x-auto font-mono leading-relaxed">
{`<!-- Google Analytics 4 - Helvetik Journal -->
<script async src="https://www.googletagmanager.com/gtag/js?id=${measurementId}"></script>
<script>
  window.dataLayer = window.dataLayer || [];
  function gtag(){dataLayer.push(arguments);}
  gtag('js', new Date());
  gtag('config', '${measurementId}', {
    page_title: document.title,
    page_location: window.location.href
  });
</script>`}
                  </pre>
                </div>
              ) : (
                <div className="bg-hj-gray-light rounded-lg p-6 text-center">
                  <i className="ri-code-line text-3xl text-hj-gray-border mb-2 block"></i>
                  <p className="text-sm text-hj-gray font-body">
                    Speichern Sie zuerst eine Measurement ID im Tab &quot;GA4 Einrichten&quot;.
                  </p>
                  <button
                    onClick={() => setActiveTab("setup")}
                    className="mt-3 text-sm text-hj-red hover:underline font-body"
                  >
                    Zur Einrichtung →
                  </button>
                </div>
              )}
            </div>

            <div className="bg-white rounded-lg border border-hj-gray-border p-5 md:p-6">
              <h2 className="text-base font-bold text-hj-dark mb-2 font-heading">
                Wie funktioniert die Integration?
              </h2>
              <div className="space-y-3 text-sm text-hj-gray font-body">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-hj-red/10 flex items-center justify-center shrink-0 mt-0.5">
                    <span className="text-xs font-bold text-hj-red">1</span>
                  </div>
                  <p>Die Measurement ID wird in <code className="bg-hj-gray-light px-1 rounded text-xs font-mono">localStorage</code> gespeichert.</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-hj-red/10 flex items-center justify-center shrink-0 mt-0.5">
                    <span className="text-xs font-bold text-hj-red">2</span>
                  </div>
                  <p>Beim App-Start liest <code className="bg-hj-gray-light px-1 rounded text-xs font-mono">App.tsx</code> die ID und injiziert das GA4-Script dynamisch in den <code className="bg-hj-gray-light px-1 rounded text-xs font-mono">&lt;head&gt;</code>.</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-hj-red/10 flex items-center justify-center shrink-0 mt-0.5">
                    <span className="text-xs font-bold text-hj-red">3</span>
                  </div>
                  <p>Bei jedem Seitenwechsel (React Router) wird automatisch ein <code className="bg-hj-gray-light px-1 rounded text-xs font-mono">page_view</code> Event an GA4 gesendet.</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-hj-red/10 flex items-center justify-center shrink-0 mt-0.5">
                    <span className="text-xs font-bold text-hj-red">4</span>
                  </div>
                  <p>Admin-Seiten (<code className="bg-hj-gray-light px-1 rounded text-xs font-mono">/admin/*</code>) werden vom Tracking ausgeschlossen.</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── Schema Tab ── */}
        {activeTab === "schema" && (
          <div className="space-y-5">
            <div className="bg-white rounded-lg border border-hj-gray-border p-5 md:p-6">
              <div className="flex items-start justify-between gap-4 mb-4">
                <div>
                  <h2 className="text-base font-bold text-hj-dark font-heading">
                    Supabase Datenbank-Schema
                  </h2>
                  <p className="text-sm text-hj-gray mt-1 font-body">
                    Führen Sie dieses SQL in Ihrem Supabase SQL-Editor aus, um die Datenbank für Helvetik Journal einzurichten.
                  </p>
                </div>
                <button
                  onClick={copySchema}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-hj-dark border border-hj-gray-border rounded-md hover:bg-hj-gray-light transition-colors whitespace-nowrap shrink-0"
                >
                  {schemaCopied ? (
                    <>
                      <i className="ri-check-line text-emerald-500"></i>
                      {t("analytics.schema.copied")}
                    </>
                  ) : (
                    <>
                      <i className="ri-clipboard-line"></i>
                      {t("analytics.schema.copy")}
                    </>
                  )}
                </button>
              </div>

              <div className="mb-4 flex flex-wrap gap-2">
                {["articles Tabelle", "authors Tabelle", "Row Level Security", "RLS Policies"].map((badge) => (
                  <span key={badge} className="px-2.5 py-1 text-xs font-medium bg-hj-gray-light text-hj-dark rounded-full font-body">
                    {badge}
                  </span>
                ))}
              </div>

              <pre className="bg-hj-dark text-green-400 text-xs p-4 rounded-lg overflow-x-auto font-mono leading-relaxed max-h-[500px] overflow-y-auto">
                {schemaSQL}
              </pre>
            </div>

            <div className="bg-white rounded-lg border border-hj-gray-border p-5 md:p-6">
              <h2 className="text-base font-bold text-hj-dark mb-4 font-heading">
                Einrichtungsanleitung
              </h2>
              <div className="space-y-4">
                {[
                  {
                    step: 1,
                    title: "Supabase Projekt öffnen",
                    desc: "Gehen Sie zu supabase.com und öffnen Sie Ihr Projekt.",
                    link: "https://supabase.com/dashboard",
                    linkLabel: "Supabase Dashboard",
                  },
                  {
                    step: 2,
                    title: "SQL Editor öffnen",
                    desc: "Klicken Sie im linken Menü auf \"SQL Editor\" und dann auf \"New query\".",
                  },
                  {
                    step: 3,
                    title: "Schema einfügen und ausführen",
                    desc: "Kopieren Sie das SQL oben, fügen Sie es in den Editor ein und klicken Sie auf \"Run\".",
                  },
                  {
                    step: 4,
                    title: "Umgebungsvariablen setzen",
                    desc: "Stellen Sie sicher, dass VITE_SUPABASE_URL und VITE_SUPABASE_ANON_KEY in Ihren Readdy-Einstellungen gesetzt sind.",
                  },
                ].map((item) => (
                  <div key={item.step} className="flex gap-4">
                    <div className="shrink-0 w-7 h-7 rounded-full bg-hj-red/10 flex items-center justify-center">
                      <span className="text-xs font-bold text-hj-red">{item.step}</span>
                    </div>
                    <div className="flex-1 min-w-0 pt-0.5">
                      <p className="text-sm font-semibold text-hj-dark font-body">{item.title}</p>
                      <p className="text-sm text-hj-gray mt-0.5 font-body">{item.desc}</p>
                      {item.link && (
                        <a
                          href={item.link}
                          target="_blank"
                          rel="noopener noreferrer nofollow"
                          className="inline-flex items-center gap-1 mt-1 text-xs text-hj-red hover:underline font-body"
                        >
                          <i className="ri-external-link-line"></i>
                          {item.linkLabel}
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Remove Confirm Modal */}
      {showRemoveConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center shrink-0">
                <i className="ri-alert-line text-red-500 text-lg"></i>
              </div>
              <h3 className="text-lg font-bold text-hj-dark font-heading">{t("analytics.remove.title")}</h3>
            </div>
            <p className="text-sm text-hj-gray mb-6 font-body">
              {t("analytics.remove.description")}
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowRemoveConfirm(false)}
                className="flex-1 px-4 py-2.5 text-sm font-medium text-hj-gray border border-hj-gray-border rounded-md hover:bg-gray-50 transition-colors"
              >
                {t("analytics.remove.cancel")}
              </button>
              <button
                onClick={handleRemove}
                className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-red-500 rounded-md hover:bg-red-600 transition-colors"
              >
                {t("analytics.status.remove")}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
